import { useState, useCallback, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { l202Api, FL120, FL120WithRefs } from '../../../services/l202Api';

export type Mode = 'browse' | 'add' | 'edit';

export function useL202Form() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FL120[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [docCode, setDocCode] = useState('');          // FL120_01 — used in add mode
  const [name, setName] = useState('');                 // FL120_11
  const [parentDoc, setParentDoc] = useState('');       // FL120_12 — 隸屬文件代號
  const [attachType, setAttachType] = useState<number>(0); // FL120_17 — 附件類型
  const [content, setContent] = useState('');            // FL120_21 — 章節內容 (MEMO)

  const [refData, setRefData] = useState<FL120WithRefs | null>(null);

  // Filter: which document to list chapters for
  const [filterDoc, setFilterDoc] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Computed: line count of content (l_memline)
  const memline = useMemo(() => {
    const c = content || '';
    return c ? c.split('\n').length : 0;
  }, [content]);

  // Load FL020 reference data
  const loadRefData = useCallback(async (docCode: string, seqNo: number) => {
    try {
      const ref = await l202Api.get(docCode, seqNo);
      setRefData(ref);
    } catch {
      setRefData(null);
    }
  }, []);

  // Load records — optionally filtered by document code
  const loadRecords = useCallback(async (doc?: string) => {
    setLoading(true);
    try {
      const res = await l202Api.list(1, 1000, undefined, doc || filterDoc || undefined);
      const data = res.data || [];
      setRecords(data);
      return data;
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, [filterDoc]);

  // Sync form fields from current record
  const syncFields = useCallback((r: FL120) => {
    setDocCode(r.fl120_01);
    setName(r.fl120_11);
    setParentDoc(r.fl120_12 || '');
    setAttachType(r.fl120_17 || 0);
    setContent(r.fl120_21 || '');
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      syncFields(current);
      loadRefData(current.fl120_01, current.fl120_02);
    }
  }, [current, mode, syncFields, loadRefData]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setDocCode(filterDoc || '');
    setName('');
    setParentDoc('');
    setAttachType(0);
    setContent('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFields(current);
  };

  // Cancel — restore from current record
  const cancel = () => {
    setMode('browse');
    if (current) syncFields(current);
  };

  // Apply filter: reload records for a specific document
  const applyFilter = async (doc: string) => {
    setFilterDoc(doc);
    const data = await loadRecords(doc);
    if (data.length > 0) setCurrentIdx(0);
    else setCurrentIdx(-1);
  };

  // Save (create or update)
  const save = async () => {
    try {
      if (mode === 'add') {
        await l202Api.create({
          fl120_01: docCode,
          fl120_11: name,
          fl120_12: parentDoc,
          fl120_17: attachType,
          fl120_21: content,
        });
        message.success('新增成功');
      } else {
        await l202Api.update(current!.fl120_01, current!.fl120_02, {
          fl120_11: name,
          fl120_12: parentDoc,
          fl120_17: attachType,
          fl120_21: content,
        });
        message.success('修改成功');
      }
      const targetDoc = mode === 'add' ? docCode : current!.fl120_01;
      const targetSeq = mode === 'add' ? undefined : current!.fl120_02;
      const data = await loadRecords(filterDoc || targetDoc);
      if (targetSeq !== undefined) {
        const idx = data.findIndex(r => r.fl120_01 === current!.fl120_01 && r.fl120_02 === targetSeq);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else {
        setCurrentIdx(data.length > 0 ? data.length - 1 : 0);
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete current record
  const doDelete = async () => {
    if (!current) return;
    try {
      await l202Api.delete(current.fl120_01, current.fl120_02);
      message.success('刪除成功');
      const data = await loadRecords(filterDoc || current.fl120_01);
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx, refData, memline,
    docCode, setDocCode,
    name, setName,
    parentDoc, setParentDoc,
    attachType, setAttachType,
    content, setContent,
    filterDoc, setFilterDoc,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, applyFilter, loadRecords,
  };
}
